/**
 * MathMarkdown - Markdown renderer with KaTeX math support
 *
 * Pipeline: marked.js + katex.renderToString() + DOMPurify
 * This approach prevents backslash-eating that occurs with ReactMarkdown + rehype-katex.
 *
 * Features:
 * - GitHub Flavored Markdown (tables, strikethrough, task lists, autolinks)
 * - Reddit-style formatting (spoilers, superscript)
 * - KaTeX math rendering with lazy loading
 * - HTML passthrough (<sup>, <sub>, <mark>, etc.)
 * - Syntax highlighting for code blocks
 * - Responsive typography
 */

import { memo, useMemo, useEffect, useState, useRef } from 'react';
import { resolveMediaUrl, resolveMediaInMarkdown } from '../../utils/media-url';

// Track if KaTeX CSS has been loaded
let katexCssLoaded = false;

/**
 * Lazily load KaTeX CSS via link tag
 */
function loadKatexCss(): void {
  if (katexCssLoaded || typeof document === 'undefined') return;

  const existing = document.querySelector('link[href*="katex"]');
  if (existing) {
    katexCssLoaded = true;
    return;
  }

  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = 'https://cdn.jsdelivr.net/npm/katex@0.16.9/dist/katex.min.css';
  link.integrity = 'sha384-n8MVd4RsNIU0tAv4ct0nTaAbDJwPJzDEaqSD1odI+WdtXRGWt2kTvGFasHpSy3SV';
  link.crossOrigin = 'anonymous';
  document.head.appendChild(link);
  katexCssLoaded = true;
}

/** Detect if text contains math delimiters */
function hasMathContent(text: string): boolean {
  return /\\\[[\s\S]+?\\\]|\\\([\s\S]+?\\\)|\$\$[\s\S]+?\$\$|\$[^$\n]+?\$|\\begin\{/.test(text);
}

/** Detect if text contains LaTeX structural commands (not just math) */
function hasLatexStructure(text: string): boolean {
  return /\\(?:section|subsection|subsubsection|textbf|textit|emph|cite|href|begin\{(?:itemize|enumerate|description)\})/
    .test(text);
}

/**
 * Convert LaTeX structural commands to Markdown equivalents.
 * Leaves math environments untouched for KaTeX to handle.
 */
function latexStructureToMarkdown(text: string): string {
  let result = text;

  // Headings
  result = result.replace(/\\section\*?\{([^}]+)\}/g, '\n## $1\n');
  result = result.replace(/\\subsection\*?\{([^}]+)\}/g, '\n### $1\n');
  result = result.replace(/\\subsubsection\*?\{([^}]+)\}/g, '\n#### $1\n');

  // Text formatting
  result = result.replace(/\\textbf\{([^}]+)\}/g, '**$1**');
  result = result.replace(/\\textit\{([^}]+)\}/g, '*$1*');
  result = result.replace(/\\emph\{([^}]+)\}/g, '*$1*');
  result = result.replace(/\\underline\{([^}]+)\}/g, '<u>$1</u>');
  result = result.replace(/\\texttt\{([^}]+)\}/g, '`$1`');

  // Links and references
  result = result.replace(/\\href\{([^}]+)\}\{([^}]+)\}/g, '[$2]($1)');
  result = result.replace(/\\url\{([^}]+)\}/g, '[$1]($1)');
  result = result.replace(/\\cite\{([^}]+)\}/g, '[$1]');

  // Lists: itemize → unordered
  result = result.replace(/\\begin\{itemize\}/g, '');
  result = result.replace(/\\end\{itemize\}/g, '');
  result = result.replace(/\\begin\{enumerate\}/g, '');
  result = result.replace(/\\end\{enumerate\}/g, '');
  result = result.replace(/\\begin\{description\}/g, '');
  result = result.replace(/\\end\{description\}/g, '');
  result = result.replace(/\\item\[([^\]]+)\]/g, '- **$1**: ');
  result = result.replace(/\\item\s*/g, '- ');

  // Common LaTeX commands to strip or convert
  result = result.replace(/\\(?:documentclass|usepackage|maketitle|tableofcontents)\{[^}]*\}\s*/g, '');
  result = result.replace(/\\(?:documentclass|usepackage)\[[^\]]*\]\{[^}]*\}\s*/g, '');
  result = result.replace(/\\begin\{document\}\s*/g, '');
  result = result.replace(/\\end\{document\}\s*/g, '');
  result = result.replace(/\\begin\{abstract\}/g, '\n> **Abstract**\n> ');
  result = result.replace(/\\end\{abstract\}/g, '\n');
  result = result.replace(/\\title\{([^}]+)\}/g, '# $1\n');
  result = result.replace(/\\author\{([^}]+)\}/g, '*$1*\n');
  result = result.replace(/\\date\{([^}]+)\}/g, '*$1*\n');

  // Line breaks and spacing
  result = result.replace(/\\\\/g, '\n');
  result = result.replace(/\\newline/g, '\n');
  result = result.replace(/\\par\b/g, '\n\n');
  result = result.replace(/\\noindent\s*/g, '');

  // Footnotes
  result = result.replace(/\\footnote\{([^}]+)\}/g, ' ($1)');

  // Clean up excess blank lines
  result = result.replace(/\n{3,}/g, '\n\n');

  return result;
}

// Lazy-loaded modules
let markedModule: typeof import('marked') | null = null;
let katexModule: typeof import('katex') | null = null;
let dompurifyModule: { default: { sanitize: (html: string, config?: Record<string, unknown>) => string } } | null = null;

interface MathMarkdownProps {
  children: string;
  className?: string;
}

/**
 * MathMarkdown component - marked.js + KaTeX + DOMPurify pipeline
 */
function MathMarkdownInner({ children, className = '' }: MathMarkdownProps) {
  const [coreReady, setCoreReady] = useState(!!markedModule && !!dompurifyModule);
  const [mathReady, setMathReady] = useState(!!katexModule);
  const containerRef = useRef<HTMLDivElement>(null);
  const hasMath = useMemo(() => hasMathContent(children), [children]);

  // Load core dependencies (marked + dompurify) — always needed
  useEffect(() => {
    if (coreReady) return;

    const loads: Promise<void>[] = [];
    if (!markedModule) {
      loads.push(import('marked').then((m) => { markedModule = m; }));
    }
    if (!dompurifyModule) {
      loads.push(import('dompurify').then((m) => { dompurifyModule = m as typeof dompurifyModule; }));
    }

    if (loads.length > 0) {
      Promise.all(loads).then(() => setCoreReady(true));
    } else {
      setCoreReady(true);
    }
  }, [coreReady]);

  // Load KaTeX when math content is detected — separate from core
  useEffect(() => {
    if (mathReady || !hasMath) return;
    loadKatexCss();
    import('katex').then((m) => { katexModule = m; setMathReady(true); });
  }, [hasMath, mathReady]);

  const html = useMemo(() => {
    if (!coreReady || !markedModule || !dompurifyModule) return null;
    if (hasMath && !mathReady) return null; // Wait for KaTeX before rendering math

    const { marked } = markedModule;
    const DOMPurify = dompurifyModule.default;
    const katex = katexModule;

    let text = resolveMediaInMarkdown(children);

    // Step 0: Convert LaTeX structural commands to Markdown (before code block protection)
    if (hasLatexStructure(text)) {
      text = latexStructureToMarkdown(text);
    }

    // Step 1: Protect code blocks with placeholders
    const codeBlocks: string[] = [];
    // Fenced code blocks
    text = text.replace(/```[\s\S]*?```/g, (match) => {
      codeBlocks.push(match);
      return `%%CODEBLOCK_${codeBlocks.length - 1}%%`;
    });
    // Inline code
    text = text.replace(/`[^`\n]+`/g, (match) => {
      codeBlocks.push(match);
      return `%%CODEBLOCK_${codeBlocks.length - 1}%%`;
    });

    // Step 2: Render LaTeX with KaTeX if available
    // KaTeX output is complex HTML that marked.js can misparse, so we protect
    // rendered math with placeholders (just like code blocks) and restore after marked.
    const mathBlocks: string[] = [];
    const protectMath = (html: string): string => {
      mathBlocks.push(html);
      return `%%MATHBLOCK_${mathBlocks.length - 1}%%`;
    };

    if (katex) {
      // Display math: \[...\] and $$...$$
      text = text.replace(/\\\[([\s\S]*?)\\\]/g, (_, expr) => {
        try {
          return protectMath(katex.renderToString(expr.trim(), { displayMode: true, throwOnError: false }));
        } catch { return `<pre class="md-code md-code--inline">${expr}</pre>`; }
      });
      text = text.replace(/\$\$([\s\S]*?)\$\$/g, (_, expr) => {
        try {
          return protectMath(katex.renderToString(expr.trim(), { displayMode: true, throwOnError: false }));
        } catch { return `<pre class="md-code md-code--inline">${expr}</pre>`; }
      });

      // Inline math: \(...\) and $...$
      text = text.replace(/\\\(([\s\S]*?)\\\)/g, (_, expr) => {
        try {
          return protectMath(katex.renderToString(expr.trim(), { displayMode: false, throwOnError: false }));
        } catch { return `<code class="md-code md-code--inline">${expr}</code>`; }
      });
      // Single $ - be careful not to match $$
      text = text.replace(/(?<!\$)\$(?!\$)([^$\n]+?)\$(?!\$)/g, (_, expr) => {
        try {
          return protectMath(katex.renderToString(expr.trim(), { displayMode: false, throwOnError: false }));
        } catch { return `<code class="md-code md-code--inline">${expr}</code>`; }
      });

      // LaTeX environments
      text = text.replace(
        /\\begin\{(equation|align|gather|multline)\*?\}([\s\S]*?)\\end\{\1\*?\}/g,
        (_, env, expr) => {
          try {
            return protectMath(katex.renderToString(`\\begin{${env}}${expr}\\end{${env}}`, { displayMode: true, throwOnError: false }));
          } catch { return `<pre class="md-code">${expr}</pre>`; }
        }
      );
    }

    // Step 2b: Reddit-style extensions
    // Spoiler tags: >!text!<
    text = text.replace(/>!([^!]+)!</g, '<span class="md-spoiler">$1</span>');
    // Superscript: ^word or ^(multiple words)
    text = text.replace(/\^(\([^)]+\)|\S+)/g, (_, content) => {
      const inner = content.startsWith('(') ? content.slice(1, -1) : content;
      return `<sup>${inner}</sup>`;
    });

    // Step 3: Parse markdown with marked
    const renderer = new marked.Renderer();

    renderer.code = ({ text: code, lang }) => {
      const langClass = lang ? ` language-${lang}` : '';
      const escaped = code.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
      return `<pre class="md-code-block"><code class="md-code${langClass}">${escaped}</code></pre>`;
    };

    renderer.codespan = ({ text: code }) => {
      return `<code class="md-code md-code--inline">${code}</code>`;
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (renderer as any).table = (token: any) => {
      // marked v17+: token.header and token.rows are arrays of cell objects
      const renderCell = (cell: any, tag: string) => {
        const align = cell.align ? ` style="text-align:${cell.align}"` : '';
        const text = marked.parseInline(cell.tokens?.map((t: any) => t.raw).join('') ?? cell.text ?? '') as string;
        return `<${tag}${align}>${text}</${tag}>`;
      };
      const headerHtml = Array.isArray(token.header)
        ? `<tr>${token.header.map((c: any) => renderCell(c, 'th')).join('')}</tr>`
        : (token.header ?? '');
      const rowsHtml = Array.isArray(token.rows)
        ? token.rows.map((row: any[]) => `<tr>${row.map((c: any) => renderCell(c, 'td')).join('')}</tr>`).join('')
        : (token.rows ?? token.body ?? '');
      return `<div class="md-table-wrapper"><table class="md-table"><thead>${headerHtml}</thead><tbody>${rowsHtml}</tbody></table></div>`;
    };

    renderer.blockquote = ({ text: quote }) => {
      return `<blockquote class="md-blockquote">${quote}</blockquote>`;
    };

    renderer.link = ({ href, text: linkText }) => {
      const isExternal = href?.startsWith('http');
      const target = isExternal ? ' target="_blank" rel="noopener noreferrer"' : '';
      return `<a href="${href}" class="md-link"${target}>${linkText}</a>`;
    };

    renderer.image = ({ href, text: alt }) => {
      const resolvedHref = resolveMediaUrl(href);
      return `<img src="${resolvedHref}" alt="${alt || ''}" class="md-image" loading="lazy" onerror="this.style.display='none';this.parentElement?.classList.add('md-image--broken')" />`;
    };

    renderer.hr = () => '<hr class="md-divider" />';

    marked.setOptions({ renderer, gfm: true, breaks: false });

    let result = marked.parse(text) as string;

    // Step 4: Restore code blocks and math blocks
    result = result.replace(/%%CODEBLOCK_(\d+)%%/g, (_, idx) => {
      const block = codeBlocks[parseInt(idx, 10)];
      if (!block) return '';
      // Re-parse just this code block through marked
      return marked.parse(block) as string;
    });
    result = result.replace(/%%MATHBLOCK_(\d+)%%/g, (_, idx) => {
      return mathBlocks[parseInt(idx, 10)] ?? '';
    });

    // Step 5: Sanitize with DOMPurify - allow KaTeX output + HTML tags
    result = DOMPurify.sanitize(result, {
      ADD_TAGS: ['semantics', 'annotation', 'mrow', 'mi', 'mo', 'mn', 'msup', 'msub', 'mfrac', 'mover', 'munder', 'munderover', 'msqrt', 'mroot', 'mtable', 'mtr', 'mtd', 'mtext', 'mspace', 'mpadded', 'menclose', 'mglyph', 'mstyle', 'merror', 'mphantom', 'mmultiscripts', 'mprescripts', 'none', 'audio', 'video', 'source'],
      ADD_ATTR: ['mathvariant', 'encoding', 'xmlns', 'display', 'accent', 'accentunder', 'columnalign', 'columnlines', 'columnspacing', 'rowlines', 'rowspacing', 'frame', 'framespacing', 'stretchy', 'symmetric', 'maxsize', 'minsize', 'largeop', 'movablelimits', 'fence', 'separator', 'lspace', 'rspace', 'depth', 'height', 'width', 'voffset', 'scriptlevel', 'linethickness', 'controls', 'preload', 'src', 'type'],
      ALLOWED_URI_REGEXP: /^(?:(?:https?|ftp|mailto|tel|data|humanizer-media):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i,
    } as Record<string, unknown>);

    return result;
  }, [children, coreReady, mathReady, hasMath]);

  if (!html) {
    // Fallback while loading: render plain text
    return (
      <div className={`md-content ${className}`}>
        <p>{children}</p>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={`md-content ${className}`}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

export const MathMarkdown = memo(MathMarkdownInner);
export { latexStructureToMarkdown };
export default MathMarkdown;
